import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Award, Maximize2, Loader2, Star, Trophy, Sparkles } from 'lucide-react';
import { apiRequest } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

export default function RecognitionWidget({ expandable, expandPath }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const result = await apiRequest('/recognition/dashboard/widget');
      setData(result);
    } catch (error) {
      console.error('Failed to fetch recognition data');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="recognition-widget">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="h-5 w-5 text-amber-500" />
            <CardTitle className="text-lg">Recognition</CardTitle>
          </div>
          {expandable && (
            <Button variant="ghost" size="icon" onClick={() => navigate(expandPath || '/admin/recognition')} className="h-8 w-8">
              <Maximize2 className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : (
          <div className="space-y-3">
            {/* My Stats */}
            <div className="flex gap-3 text-center">
              <div className="flex-1 p-2 bg-amber-50 rounded">
                <div className="flex items-center justify-center gap-1">
                  <Star className="h-4 w-4 text-amber-500" />
                  <span className="text-lg font-bold text-amber-700">{data?.my_recognition_count || 0}</span>
                </div>
                <p className="text-xs text-amber-600">Recognitions</p>
              </div>
              <div className="flex-1 p-2 bg-purple-50 rounded">
                <div className="flex items-center justify-center gap-1">
                  <Trophy className="h-4 w-4 text-purple-500" />
                  <span className="text-lg font-bold text-purple-700">{data?.my_reward_count || 0}</span>
                </div>
                <p className="text-xs text-purple-600">Rewards</p>
              </div>
            </div>

            {/* Recent Activity */}
            {data?.recent_recognitions?.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs font-medium text-slate-500">Recent Activity</p>
                {data.recent_recognitions.slice(0, 2).map(item => (
                  <div key={item.id} className="flex items-center gap-2 p-2 bg-slate-50 rounded text-xs">
                    <span className="text-lg">{item.badge_emoji}</span>
                    <div className="flex-1 min-w-0">
                      <span className="font-medium">{item.giver_name}</span>
                      {' → '}
                      <span className="font-medium">{item.recipient_name}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-2 text-slate-400">
                <Sparkles className="h-6 w-6 mx-auto mb-1 opacity-50" />
                <p className="text-xs">No recent recognitions</p>
              </div>
            )}

            {/* Action Button */}
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full text-xs"
              onClick={() => navigate('/admin/recognition')}
            >
              <Sparkles className="h-3 w-3 mr-1" />
              Give Recognition
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
